/**
 * Email templates store inline images as data URLs. Large uploads bloat HTML,
 * slow saves, and hit ESP limits. Downscale rasters in the browser before embedding.
 */

/** Logos display small; keep stored bitmap modest. */
export const EMAIL_LOGO_MAX_PIXEL_SIDE = 600

/** Hero / body images: ~2× typical email column width for retina without huge files. */
export const EMAIL_INLINE_IMAGE_MAX_PIXEL_SIDE = 1200

const DEFAULT_JPEG_QUALITY_LOGO = 0.88
const DEFAULT_JPEG_QUALITY_INLINE = 0.85

/**
 * If the longest edge exceeds `maxPixelSide`, redraw proportionally and return a new data URL.
 * PNG stays PNG; other raster types use JPEG at `jpegQuality`. SVG and failures return input.
 */
export function downscaleDataUrlForEmailRaster(
  dataUrl: string,
  maxPixelSide: number,
  jpegQuality: number = DEFAULT_JPEG_QUALITY_LOGO,
): Promise<string> {
  if (
    !dataUrl.startsWith('data:image') ||
    dataUrl.startsWith('data:image/svg')
  ) {
    return Promise.resolve(dataUrl)
  }

  return new Promise((resolve) => {
    const img = new Image()
    img.onload = (): void => {
      try {
        const w = img.naturalWidth
        const h = img.naturalHeight
        if (!w || !h) {
          resolve(dataUrl)
          return
        }
        const longest = Math.max(w, h)
        if (longest <= maxPixelSide) {
          resolve(dataUrl)
          return
        }
        const scale = maxPixelSide / longest
        const tw = Math.max(1, Math.round(w * scale))
        const th = Math.max(1, Math.round(h * scale))
        const canvas = document.createElement('canvas')
        canvas.width = tw
        canvas.height = th
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          resolve(dataUrl)
          return
        }
        ctx.drawImage(img, 0, 0, tw, th)
        const isPng = /^data:image\/png/i.test(dataUrl)
        const out = isPng
          ? canvas.toDataURL('image/png')
          : canvas.toDataURL('image/jpeg', jpegQuality)
        resolve(out || dataUrl)
      } catch {
        resolve(dataUrl)
      }
    }
    img.onerror = (): void => resolve(dataUrl)
    img.src = dataUrl
  })
}

/** Logo and click-to-replace logo slots (smaller cap). */
export function downscaleDataUrlForEmailLogo(dataUrl: string): Promise<string> {
  return downscaleDataUrlForEmailRaster(
    dataUrl,
    EMAIL_LOGO_MAX_PIXEL_SIDE,
    DEFAULT_JPEG_QUALITY_LOGO,
  )
}

/** Elements → Image and hero block uploads (larger cap for full-width graphics). */
export function downscaleDataUrlForEmailInlineImage(
  dataUrl: string,
): Promise<string> {
  return downscaleDataUrlForEmailRaster(
    dataUrl,
    EMAIL_INLINE_IMAGE_MAX_PIXEL_SIDE,
    DEFAULT_JPEG_QUALITY_INLINE,
  )
}
